// Weer: regen, mist, onweer en de bijbehorende overlays.
// Methoden worden op BezorgerGame.prototype gezet (zie game/mixins.js).

import { t } from '../i18n';
export class WeatherMixin {
  // ==========================================
  // DYNAMISCHE WEERSOMSTANDIGHEDEN (REGEN, MIST, ZONNIG)
  // ==========================================
  initWeatherParticles() {
    // 1. Raindrop particles (screen space relative)
    this.rainDrops = [];
    for (let i = 0; i < 220; i++) {
      this.rainDrops.push({
        x: Math.random() * 1200,
        y: Math.random() * 900,
        speed: 18 + Math.random() * 14,
        len: 12 + Math.random() * 16,
        thickness: 1.0 + Math.random() * 1.2,
        alpha: 0.35 + Math.random() * 0.45
      });
    }

    // 2. Rain splash ripples on the asphalt
    this.rainSplashes = [];

    // 3. Volumetric Fog Patches (World space drifting clouds)
    this.fogClouds = [];
    const worldW = this.map ? this.map.width : 2400;
    const worldH = this.map ? this.map.height : 2400;
    for (let i = 0; i < 24; i++) {
      this.fogClouds.push({
        x: Math.random() * worldW,
        y: Math.random() * worldH,
        radius: 120 + Math.random() * 160,
        vx: 3 + Math.random() * 6,
        vy: -1 + (Math.random() - 0.5) * 2,
        baseAlpha: 0.12 + Math.random() * 0.16,
        pulseOffset: Math.random() * Math.PI * 2
      });
    }
  }

  setWeather(weatherKey, showToast = true) {
    if (!['sunny', 'rain', 'mist'].includes(weatherKey)) {
      weatherKey = 'sunny';
    }
    const prevWeather = this.weather;
    this.weather = weatherKey;

    // Audio Transition
    if (this.sound) {
      this.sound.setWeather(weatherKey);
    }

    if (showToast && prevWeather !== weatherKey && this.ui) {
      this.ui.toast(t(`weather.${weatherKey}`), 'info');
    }
  }

  cycleWeather() {
    const list = ['sunny', 'rain', 'mist'];
    const idx = list.indexOf(this.weather);
    const next = list[(idx + 1) % list.length];
    this.setWeather(next, true);
  }

  updateWeatherParticles(dt) {
    // Dynamic Weather auto-cycling check
    this.weatherTimer += dt;
    if (this.weatherMode === 'dynamic' && this.weatherTimer > 110) {
      this.weatherTimer = 0;
      // 50% sunny, 30% rain, 20% mist
      const r = Math.random();
      const nextW = r < 0.5 ? 'sunny' : (r < 0.8 ? 'rain' : 'mist');
      if (nextW !== this.weather) {
        this.setWeather(nextW, true);
      }
    }

    const w = this.viewW || 800;
    const h = this.viewH || 600;

    // 1. Rain Drops & Splashes
    if (this.weather === 'rain') {
      const windSpeed = 3.5;
      for (const drop of this.rainDrops) {
        drop.y += drop.speed;
        drop.x += windSpeed;

        if (drop.y > h + 20) {
          drop.y = -20;
          drop.x = Math.random() * (w + 200) - 100;

          // Occasionally spawn a mini splash ripple on the ground
          if (Math.random() < 0.25 && this.rainSplashes.length < 35) {
            this.rainSplashes.push({
              x: drop.x,
              y: h - 10 - Math.random() * 40,
              radius: 1,
              maxRadius: 6 + Math.random() * 6,
              alpha: 0.6
            });
          }
        }
        if (drop.x > w + 100) drop.x = -50;
      }

      // Update rain splash ripples
      for (let i = this.rainSplashes.length - 1; i >= 0; i--) {
        const sp = this.rainSplashes[i];
        sp.radius += 0.45;
        sp.alpha -= 0.035;
        if (sp.alpha <= 0) {
          this.rainSplashes.splice(i, 1);
        }
      }

      // Random Thunder Lightning Event (occasional dramatic flash)
      this.thunderCooldown -= dt;
      if (this.thunderCooldown <= 0) {
        this.thunderCooldown = 18 + Math.random() * 25;
        this.thunderFlashTimer = 0.22; // 220ms flash
        if (this.sound) this.sound.playThunder();
      }
    }

    if (this.thunderFlashTimer > 0) {
      this.thunderFlashTimer -= dt;
    }

    // 2. Volumetric Fog Patches
    if (this.weather === 'mist' || this.weather === 'rain') {
      const worldW = this.map ? this.map.width : 2400;
      const worldH = this.map ? this.map.height : 2400;
      for (const fog of this.fogClouds) {
        fog.x += fog.vx * dt * 4;
        fog.y += fog.vy * dt * 4;
        fog.pulseOffset += dt * 0.8;

        if (fog.x - fog.radius > worldW) fog.x = -fog.radius;
        if (fog.x + fog.radius < 0) fog.x = worldW + fog.radius;
        if (fog.y - fog.radius > worldH) fog.y = -fog.radius;
        if (fog.y + fog.radius < 0) fog.y = worldH + fog.radius;
      }
    }
  }

  renderWorldWeather(ctx) {
    // Rendered inside the world transform (camera coordinates)
    // 1. Wet Road Reflections in Rain
    if (this.weather === 'rain' && this.map && this.map.roads) {
      ctx.save();
      // Draw glossy puddle reflections along roads
      ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
      for (const r of this.map.roads) {
        ctx.fillRect(r.x, r.y, r.w, r.h);
      }
      ctx.restore();
    }

    // 2. Volumetric Fog drifting clouds across the city blocks
    if (this.weather === 'mist') {
      ctx.save();
      for (const fog of this.fogClouds) {
        const currentAlpha = fog.baseAlpha + Math.sin(fog.pulseOffset) * 0.04;
        const grad = ctx.createRadialGradient(fog.x, fog.y, fog.radius * 0.1, fog.x, fog.y, fog.radius);
        if (this.isNight) {
          grad.addColorStop(0, `rgba(180, 200, 230, ${currentAlpha * 0.9})`);
          grad.addColorStop(0.5, `rgba(100, 130, 170, ${currentAlpha * 0.45})`);
          grad.addColorStop(1, 'rgba(50, 70, 100, 0)');
        } else {
          grad.addColorStop(0, `rgba(255, 255, 255, ${currentAlpha * 1.1})`);
          grad.addColorStop(0.6, `rgba(240, 245, 255, ${currentAlpha * 0.6})`);
          grad.addColorStop(1, 'rgba(230, 240, 250, 0)');
        }
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(fog.x, fog.y, fog.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  renderWeatherOverlay(ctx, w, h) {
    // Rendered in Screen Space after ctx.restore()

    // 1. RAIN OVERLAY (Streaks & Splash ripples)
    if (this.weather === 'rain') {
      ctx.save();
      // Ambient cool rain tint
      ctx.fillStyle = this.isNight ? 'rgba(15, 23, 42, 0.28)' : 'rgba(70, 95, 120, 0.16)';
      ctx.fillRect(0, 0, w, h);

      // Streaking rain drops
      ctx.strokeStyle = this.isNight ? 'rgba(186, 230, 253, 0.45)' : 'rgba(224, 242, 254, 0.65)';
      ctx.lineCap = 'round';

      for (const drop of this.rainDrops) {
        ctx.lineWidth = drop.thickness;
        ctx.beginPath();
        ctx.moveTo(drop.x, drop.y);
        ctx.lineTo(drop.x + 3.5, drop.y + drop.len);
        ctx.stroke();
      }

      // Splash ripples on bottom screen
      ctx.lineWidth = 1.2;
      for (const sp of this.rainSplashes) {
        ctx.strokeStyle = `rgba(224, 242, 254, ${sp.alpha})`;
        ctx.beginPath();
        ctx.ellipse(sp.x, sp.y, sp.radius * 1.6, sp.radius * 0.6, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
    }

    // 2. MIST / FOG OVERLAY (Atmospheric Depth & Vignette)
    if (this.weather === 'mist') {
      ctx.save();
      // Soft radial mist vignette
      const mistGrad = ctx.createRadialGradient(w / 2, h / 2, w * 0.2, w / 2, h / 2, w * 0.7);
      if (this.isNight) {
        mistGrad.addColorStop(0, 'rgba(30, 41, 59, 0.2)');
        mistGrad.addColorStop(0.7, 'rgba(15, 23, 42, 0.55)');
        mistGrad.addColorStop(1, 'rgba(10, 15, 26, 0.82)');
      } else {
        mistGrad.addColorStop(0, 'rgba(241, 245, 249, 0.25)');
        mistGrad.addColorStop(0.7, 'rgba(226, 232, 240, 0.58)');
        mistGrad.addColorStop(1, 'rgba(203, 213, 225, 0.85)');
      }
      ctx.fillStyle = mistGrad;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // 3. THUNDER LIGHTNING FLASH (Dramatic illumination)
    if (this.thunderFlashTimer > 0) {
      ctx.save();
      const flashAlpha = Math.min(0.75, this.thunderFlashTimer * 3.5);
      ctx.fillStyle = `rgba(240, 249, 255, ${flashAlpha})`;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // 4. SUNNY AMBIENT GLOW (Subtle warmth)
    if (this.weather === 'sunny' && !this.isNight) {
      ctx.save();
      const sunGrad = ctx.createRadialGradient(w * 0.85, h * 0.15, 10, w * 0.85, h * 0.15, w * 0.6);
      sunGrad.addColorStop(0, 'rgba(254, 243, 199, 0.15)');
      sunGrad.addColorStop(0.5, 'rgba(254, 243, 199, 0.04)');
      sunGrad.addColorStop(1, 'rgba(254, 243, 199, 0)');
      ctx.fillStyle = sunGrad;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }
  }
}
